import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash';
import {
  Container,
  Button,
  chakra,
  Center,
  Text,
  Flex,
  VStack,
  GridItem,
} from '@chakra-ui/react';
import ReactPlayer from 'react-player';

import Link from '@rsc/ui-components/tailwind/Link';
import CTAButton from '@rsc/ui-components/tailwind/CTAButton';
import Image from '@rsc/ui-components/tailwind/Image';

import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { DeptGrid } from '../../../design-system';
import DeptSelect from '../../../atoms/DeptSelect';
import RichText from '../../../molecules/RichText';

const getSpacerCols = len => {
  switch (len) {
    case 1:
      return 4;
    default:
      return 2;
  }
};

export default function DoorFlapFinder({ block }) {
  const containerRef = useRef(null);
  const { tt } = useUiComponentsInternationalContext();

  const flapOptions = useMemo(
    () =>
      block.flapGroups
        .flatMap(group =>
          group.flaps.map(flap => ({
            value: flap.title,
            name: group.sectionId,
            uniq: uniqueId(flap.title),
          }))
        )
        .sort((a, b) => a.value.localeCompare(b.value)),
    [block]
  );

  const skuOptions = useMemo(
    () =>
      block.flapGroups.flatMap(group =>
        group.flaps
          .flatMap(flap =>
            flap.skus.map(sku => ({
              value: sku,
              name: group.sectionId,
              uniq: uniqueId(sku),
            }))
          )
          .sort((a, b) => a.value.localeCompare(b.value))
      ),
    [block]
  );

  const handleScrollByName = ({ target: { value } }) => {
    const optionSelected = flapOptions.find(opt => opt.value === value);
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      document.getElementById(optionSelected.name).scrollIntoView();

      const url = new URL(window.location);
      url.hash = optionSelected.name;
      window.history.pushState(null, '', url.toString());
    }
  };

  const handleScrollBySku = ({ target: { value } }) => {
    const optionSelected = skuOptions.find(opt => opt.value === value);
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      document.getElementById(optionSelected.name).scrollIntoView();

      const url = new URL(window.location);
      url.hash = optionSelected.name;
      window.history.pushState(null, '', url.toString());
    }
  };

  const scrollToTop = () => {
    if (containerRef?.current && typeof window !== 'undefined') {
      const url = new URL(window.location);
      url.hash = '';
      window.history.pushState(null, '', url.toString());
      containerRef.current.scrollIntoView();
    }
  };

  return (
    <Container ref={containerRef}>
      <DeptGrid mb="16">
        <GridItem colSpan={6} pt="6">
          <Text color="typography.base" textStyle="h3" as="h2" mb="8">
            {block.headline}
          </Text>
          {block.flapFinderCopy && <RichText body={block.flapFinderCopy} />}
          {block.cta && <CTAButton cta={block.cta} className="mt-4" />}
        </GridItem>
        {block.video?.url && (
          <GridItem colSpan={6}>
            <ReactPlayer
              url={block.video?.url}
              width="100%"
              style={{
                borderRadius: '8px',
              }}
            />
          </GridItem>
        )}
      </DeptGrid>
      <DeptGrid my="16">
        <GridItem colSpan={6}>
          <VStack w="full" align="flex-start" gap="0">
            <Text
              textStyle="caption"
              fontWeight="bold"
              pos="relative"
              top="3px"
            >
              {tt(s => s.blocksDeptComponents.searchByNameLabel)}:
            </Text>
            <DeptSelect onChange={handleScrollByName}>
              {flapOptions.map(opt => (
                <option key={opt.uniq} value={opt.value} name={opt.name}>
                  {opt.value}
                </option>
              ))}
            </DeptSelect>
          </VStack>
        </GridItem>
        <GridItem colSpan={6}>
          <VStack w="full" align="flex-start" gap="0">
            <Text
              textStyle="caption"
              fontWeight="bold"
              pos="relative"
              top="3px"
            >
              {tt(s => s.blocksDeptComponents.searchBySKULabel)}:
            </Text>
            <DeptSelect onChange={handleScrollBySku}>
              {skuOptions.map(opt => (
                <option key={opt.uniq} value={opt.value} name={opt.name}>
                  {opt.value}
                </option>
              ))}
            </DeptSelect>
          </VStack>
        </GridItem>
      </DeptGrid>
      {block.flapGroups?.map(group => (
        <DeptGrid
          key={group._key}
          id={group.sectionId}
          pb="24"
          sx={{ scrollMargin: '120px' }}
        >
          <GridItem
            colSpan={getSpacerCols(group.flaps?.length)}
            display={{
              base: 'none',
              lg: group.flaps.length === 3 ? 'none' : 'block',
            }}
          />
          {group.flaps?.map(flap => (
            <GridItem colSpan={{ base: 6, lg: 4 }} key={flap._key}>
              <VStack
                as={Link}
                className={`relative items-start p-4 gap-0 bg-white rounded-lg h-full border border-accent ${
                  flap?.link?.href ? 'border-cta' : ''
                }`}
                link={flap.link}
              >
                <Text
                  color="typography.base"
                  textStyle="h4"
                  as="h3"
                  w="full"
                  align="center"
                  my="4"
                >
                  {flap.title}
                </Text>

                <Image
                  className="mb-4 rounded-lg"
                  sanityImage={flap.image}
                  width="100%"
                />
                <Text color="typography.light" textStyle="caption">
                  {tt(s => s.blocksDeptComponents.SKUsLabel)}:{' '}
                  {flap.skus?.join(', ')}
                </Text>
                {flap.note && (
                  <Text color="typography.base" textStyle="body">
                    {flap.note}
                  </Text>
                )}
              </VStack>
            </GridItem>
          ))}
          {group.flapGroupCopy && (
            <GridItem colSpan={{ base: 6, lg: 12 }}>
              <Center display={{ base: 'block', md: 'flex' }}>
                <RichText body={group.flapGroupCopy} />
              </Center>
            </GridItem>
          )}

          <GridItem colSpan={{ base: 6, lg: 12 }}>
            <Flex w="full" justify="flex-end">
              <Button variant="base" onClick={scrollToTop}>
                {tt(s => s.blocksDeptComponents.backToTopButton)}:
              </Button>
            </Flex>
          </GridItem>
          <GridItem colSpan={{ base: 6, lg: 12 }}>
            <Center w="full">
              <chakra.hr w="80%" bg="typography.base" h="1px" my="10" />
            </Center>
          </GridItem>
        </DeptGrid>
      ))}
    </Container>
  );
}

DoorFlapFinder.propTypes = {
  block: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    flapFinderCopy: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    cta: PropTypes.shape({}),
    video: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    flapGroups: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string.isRequired,
        sectionId: PropTypes.string.isRequired,
        flaps: PropTypes.arrayOf(
          PropTypes.shape({
            _key: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            link: PropTypes.shape({
              href: PropTypes.string,
            }),
            image: PropTypes.shape({
              asset: PropTypes.shape({
                url: PropTypes.string,
              }),
            }),
            skus: PropTypes.arrayOf(PropTypes.string).isRequired,
            note: PropTypes.string,
          })
        ).isRequired,
        flapGroupCopy: PropTypes.arrayOf(PropTypes.shape({})),
      })
    ).isRequired,
  }).isRequired,
};
